<template>
	<Dialog :dialog="checkoutDialog" :dialog-width="dialogWidth">
		<template v-slot:title>Issue {{ type }}</template>
		<template v-slot:body>
			<v-form
				ref="checkoutForm"
				v-model.trim="formValid"
				lazy-validation
				v-on:submit.stop.prevent="onSubmit"
			>
				<v-row class="checkout-dialog">
					<v-col md="12" v-if="relatedDetails && relatedDetails.name">
						<perfect-scrollbar
							:options="{ suppressScrollX: true }"
							class="scroll"
							style="max-height: 60vh; position: static"
						>
							<v-list class="border border-secondary" subheader three-line>
								<v-list-item>
									<v-list-item-avatar v-if="relatedDetails">
										<ImageTemplate circle :src="relatedDetails.image"></ImageTemplate>
									</v-list-item-avatar>
									<v-list-item-content v-if="relatedDetails">
										<v-list-item-title class="font-size-16">
											<Chip color="blue darken-4" :text="relatedDetails.barcode"></Chip>
										</v-list-item-title>
										<v-list-item-subtitle class="font-size-14">
											<b>{{ relatedDetails.name }}</b>
										</v-list-item-subtitle>
									</v-list-item-content>
									<v-list-item-action class="margin-auto" v-if="relatedDetails">
										<v-list-item-action-text class="font-size-16"
											><b>Cost</b> {{ relatedDetails.cost_price_formatted }}
										</v-list-item-action-text>
									</v-list-item-action>
								</v-list-item>
							</v-list>
						</perfect-scrollbar>
					</v-col>
					<template v-if="type != 'Perishable'">
						<v-col md="3" class="my-auto py-0">
							<label :for="`checkout-duration-${uniqFieldId}`" class="btx-label mt-3">Duration</label>
						</v-col>
						<v-col md="9" class="py-0">
							<v-layout>
								<v-flex md6>
									<NumberInput
										hide-details
										:disabled="pageLoading"
										:loading="pageLoading"
										:id="`checkout-duration-${uniqFieldId}`"
										placeholder="Duration"
										v-model="checkout.duration"
										v-on:keyup="updateDuration()"
									></NumberInput>
								</v-flex>
								<v-flex md6>
									<SelectInput
										hide-details
										:items="durationList"
										:disabled="pageLoading"
										:loading="pageLoading"
										placeholder="Duration"
										v-model="checkout.duration_type"
										v-on:change="updateDuration()"
									></SelectInput>
								</v-flex>
							</v-layout>
						</v-col>
						<v-col md="3" class="my-auto py-0">
							<label :for="`return-on-${uniqFieldId}`" class="btx-label mt-3 required">Return On</label>
						</v-col>
						<v-col md="9" class="py-0">
							<DateTimePicker
								hide-details
								:disabled="(checkout.indefinitely ? true : false) || pageLoading"
								:loading="pageLoading"
								:id="`return-on-${uniqFieldId}`"
								required
								placeholder="Return On"
								v-model="checkout.return_on"
							></DateTimePicker>
						</v-col>
						<v-col md="3" class="py-0"> </v-col>
						<v-col md="9" class="py-0">
							<v-checkbox
								dense
								hide-details
								label="Issue indefinitely"
								class="mt-3 mb-0 p-0"
								:true-value="1"
								:false-value="0"
								v-model="checkout.indefinitely"
							></v-checkbox>
						</v-col>
					</template>
					<template v-if="type == 'Asset Stock' || type == 'Perishable'">
						<v-col md="3" class="my-auto py-0">
							<label :for="`member-${uniqFieldId}`" class="btx-label mt-3 required">Quantity</label>
						</v-col>
						<v-col md="9" class="py-0">
							<NumberInput
								hide-details
								:disabled="pageLoading"
								:loading="pageLoading"
								:id="`checkout-duration-${uniqFieldId}`"
								placeholder="Quantity"
								:rules="[vrules.required(checkout.quantity, 'Group')]"
								:class="{
									required: !checkout.quantity,
								}"
								v-model="checkout.quantity"
							></NumberInput>
						</v-col>
					</template>
					<v-col md="3" class="my-auto py-0">
						<label :for="`member-${uniqFieldId}`" class="btx-label mt-3 required">Issue To</label>
					</v-col>
					<v-col md="9" class="py-0">
						<AutoCompleteInput
							hide-details
							:items="memberList"
							:disabled="pageLoading"
							:loading="pageLoading"
							:id="`member-${uniqFieldId}`"
							:rules="[vrules.required(checkout.member, 'Issue To')]"
							:class="{
								required: !checkout.member,
							}"
							placeholder="Incharge Officer"
							v-model="checkout.member"
						></AutoCompleteInput>
					</v-col>
					<template v-if="false">
						<v-col md="3" class="my-auto py-0">
							<label :for="`location-${uniqFieldId}`" class="btx-label mt-3">Location</label>
						</v-col>
						<v-col md="9" class="py-0">
							<AutoCompleteInput
								hide-details
								:items="locationList"
								:disabled="pageLoading"
								:loading="pageLoading"
								:id="`location-${uniqFieldId}`"
								placeholder="Location"
								v-model="checkout.location"
								append-outer-icon="mdi-cog"
								v-on:click:append-outer="manageLocation()"
							></AutoCompleteInput>
						</v-col>
					</template>
					<v-col md="3" class="py-0">
						<label :for="`comment-${uniqFieldId}`" class="btx-label mt-3">Comments</label>
					</v-col>
					<v-col md="9" class="py-0">
						<TextAreaInput
							hide-details
							:disabled="pageLoading"
							:loading="pageLoading"
							:id="`comment-${uniqFieldId}`"
							placeholder="Comments"
							v-model="checkout.comment"
						></TextAreaInput>
					</v-col>
					<v-col md="3" class="py-0">
						<label :for="`attachment-${uniqFieldId}`" class="btx-label mt-3">Attachments</label>
					</v-col>
					<v-col md="9" class="py-0">
						<v-file-input
							:id="`attachment-${uniqFieldId}`"
							v-model="attachment"
							color="blue darken-4"
							small-chips
							placeholder="Click here to select files"
							multiple
							prepend-icon=""
							class="ctx-attachment-field mt-3 pt-0"
							hide-details
							:disabled="pageLoading"
							:loading="pageLoading"
							prepend-inner-icon="mdi-attachment mdi-rotate-315"
							outlined
							v-on:change="limitAttachment($event)"
						></v-file-input>
						<span class="font-small">Max 3 files allowed.</span>
					</v-col>
					<v-col md="3" class="py-0">
						<label :for="`reason-${uniqFieldId}`" class="btx-label mt-3">Reason</label>
					</v-col>
					<v-col md="9" class="py-0">
						<TextAreaInput
							hide-details
							:disabled="pageLoading"
							:loading="pageLoading"
							:id="`reason-${uniqFieldId}`"
							placeholder="Reason"
							v-model="checkout.reason"
						></TextAreaInput>
					</v-col>
					<template v-if="false">
						<v-col md="3" class="py-0 mt-2">
							<label :for="`comment-${uniqFieldId}`" class="btx-label mt-3">Signature</label>
						</v-col>
						<v-col md="9" class="py-0">
							<div class="signature-pad" id="signature-pad">
								<div ref="signature_div" class="custom-border-grey-dashed mt-2" style="width: 100%">
									<canvas ref="cc_signature"></canvas>
								</div>
							</div>
						</v-col>
					</template>

					<template v-if="type != 'Perishable'">
						<v-col md="3" class="py-0"> </v-col>
						<v-col md="9" class="py-0">
							<v-checkbox
								dense
								hide-details
								label="Ignore conflicting reservations"
								:true-value="1"
								:false-value="0"
								class="mt-3 mb-0 p-0"
								v-model="checkout.ignore_conflict"
							></v-checkbox>
						</v-col>
						<v-col md="3" class="py-0"> </v-col>
						<v-col md="9" class="py-0">
							<v-checkbox
								dense
								hide-details
								label="Fulfill reservation on this Asset for the selected user, if any"
								class="mt-3 mb-0 p-0"
								:true-value="1"
								:false-value="0"
								v-model="checkout.ful_fill"
							></v-checkbox>
						</v-col>
						<v-col md="3" class="py-0"> </v-col>
						<v-col md="9" class="py-0">
							<v-checkbox
								dense
								hide-details
								label="Show Reservations"
								class="mt-3 mb-0 p-0"
								v-model="showReservation"
								v-on:change="getReservations()"
							></v-checkbox>
						</v-col>
					</template>
					<template v-if="showReservation">
						<v-col md="12" class="font-level-3-bold pb-3 text-black border-bottom-light-grey mt-4 py-0">
							Reservations
						</v-col>
						<v-col md="12" class="py-0">
							<div
								class="mt-3 overflow-y border-light-grey"
								style="max-height: calc(100vh - 337px); min-height: 200px"
							>
								<table width="100%" class="detail-table table-head-sticky">
									<thead>
										<tr>
											<th width="40%" class="p-2 font-level-1-bold light-dark-blue-bg">Requested By</th>
											<th width="30%" class="p-2 font-level-1-bold light-dark-blue-bg">From</th>
											<th width="30%" class="p-2 font-level-1-bold light-dark-blue-bg">To</th>
										</tr>
									</thead>
									<tbody v-if="reservationLoading">
										<tr>
											<td colspan="3">
												<v-progress-linear color="orange darken-2" indeterminate height="4"></v-progress-linear>
											</td>
										</tr>
									</tbody>
									<tbody v-else-if="reservationList.length">
										<tr v-for="(row, index) in reservationList" :key="index">
											<td width="40%" class="px-2 py-3 font-level-1 text-black border-top-light-grey">
												{{ row.member_display_name }}
											</td>
											<td width="30%" class="px-2 py-3 font-level-1 text-black border-top-light-grey">
												{{ row.reserve_from_formatted }}
											</td>
											<td width="30%" class="px-2 py-3 font-level-1 text-black border-top-light-grey">
												{{ row.reserve_to_formatted }}
											</td>
										</tr>
									</tbody>
									<tfoot v-else>
										<tr>
											<td colspan="3">
												<p class="m-0 row-not-found text-center">
													<img :src="$assetURL('media/error/empty.png')" class="row-not-found-image" />
													Uhh... There are no reservation at the moment.
												</p>
											</td>
										</tr>
									</tfoot>
								</table>
							</div>
						</v-col>
					</template>
				</v-row>
			</v-form>
		</template>
		<template v-slot:action>
			<template v-if="cartUuid">
				<v-btn
					class="white--text"
					:loading="pageLoading"
					:disabled="!formValid || pageLoading"
					depressed
					color="blue darken-4"
					tile
					v-on:click="updateCart()"
				>
					Update Cart
				</v-btn>
			</template>
			<template v-else>
				<v-btn
					class="white--text"
					:loading="pageLoading"
					:disabled="!formValid || pageLoading"
					depressed
					color="blue darken-4"
					tile
					v-on:click="addToCart()"
				>
					Add To Cart
				</v-btn>
			</template>
			<template v-if="false">
				<template v-if="checkoutUuid">
					<v-btn
						class="white--text"
						:loading="pageLoading"
						:disabled="!formValid || pageLoading"
						depressed
						color="blue darken-4"
						tile
						v-on:click="onUpdateSubmit()"
					>
						Issue Update
					</v-btn>
				</template>
				<template v-else>
					<v-btn
						class="white--text"
						:loading="pageLoading"
						:disabled="!formValid || pageLoading"
						depressed
						color="blue darken-4"
						tile
						v-on:click="onSubmit()"
					>
						Issue
					</v-btn>
				</template>
			</template>
			<v-btn depressed tile :disabled="pageLoading" v-on:click="$emit('close', true)"> Close </v-btn>
		</template>
	</Dialog>
</template>
<script>
import { mapGetters } from "vuex";
import { toSafeInteger } from "lodash";
import Dialog from "@/view/components/Dialog";
import ApiService from "@/core/services/api.service";
import { SET_ERROR, SET_MESSAGE } from "@/core/services/store/common.module";
import AutoCompleteInput from "@/view/components/AutoCompleteInput";
import TextAreaInput from "@/view/components/TextAreaInput";
import DateTimePicker from "@/view/components/DateTimePicker";
import NumberInput from "@/view/components/NumberInput";
import SelectInput from "@/view/components/SelectInput";
import ImageTemplate from "@/view/components/Image";
import Chip from "@/view/components/Chip";
import { EventBus } from "@/core/event-bus/event.bus";
import SignaturePad from "signature_pad";
import { CART_COUNT } from "@/core/services/store/config.module";
import { UploadFiles } from "@/core/lib/upload.lib";
import store from "@/core/services/store";
import MomentJS from "moment-timezone";

MomentJS.tz.setDefault(process.env.VUE_APP_TIMEZONE);

export default {
	name: "checkout-template",
	title: "Issue Template",
	props: {
		type: {
			type: String,
			default: null,
		},
		typeUuid: {
			type: String,
			default: null,
		},
		reservationUuid: {
			type: String,
			default: null,
		},
		endpoint: {
			type: String,
			default: null,
		},
		checkoutDialog: {
			type: Boolean,
			default: false,
		},
		uuid: {
			type: String,
			default: null,
		},
		cartUuid: {
			type: String,
			default: null,
		},
	},
	watch: {
		typeUuid: {
			deep: true,
			immediate: true,
			handler(param) {
				if (param && !this.uuid) {
					this.getRelatedDetails(this.type, param);
				}
			},
		},
		checkoutDialog: {
			deep: true,
			immediate: true,
			handler(param) {
				if (param) {
					this.init();
				}
			},
		},
	},
	data() {
		return {
			formValid: true,
			pageLoading: false,
			reservationLoading: false,
			locationList: [],
			memberList: [],
			signature: null,
			attachment: [],
			reservationList: [],
			showReservation: false,
			checkoutUuid: null,
			relatedDetails: {},
			checkout: {
				duration: 1,
				duration_type: "week",
				return_on: null,
				indefinitely: 0,
				signature: null,
				reason: null,
				attachment: [],
				member: 0,
				quantity: 0,
				location: 0,
				comment: null,
				ignore_conflict: 0,
				ful_fill: 1,
				reservation_uuid: null,
			},
		};
	},
	components: {
		Dialog,
		NumberInput,
		SelectInput,
		DateTimePicker,
		ImageTemplate,
		TextAreaInput,
		Chip,
		AutoCompleteInput,
	},
	methods: {
		limitAttachment(output) {
			if (this.pageLoading) {
				return false;
			}

			this.attachment = output.slice(0, 3);

			if (this.attachment.length <= 0) {
				return false;
			}

			const request = new FormData();

			for (let i = 0; i < this.attachment.length; i++) {
				request.append(`files[${i}]`, this.attachment[i]);
			}

			this.pageLoading = true;

			UploadFiles(request)
				.then((response) => {
					this.checkout.attachment = response;
				})
				.catch((error) => {
					this.$store.commit(SET_ERROR, [{ model: true, message: error }]);
				})
				.finally(() => {
					this.pageLoading = false;
				});
		},
		init() {
			const next_week = MomentJS().add(7, "days");
			this.checkout = new Object({
				duration: 1,
				duration_type: "week",
				return_on: this.formatDateTimeRaw(next_week),
				indefinitely: 0,
				member: 0,
				location: 0,
				comment: null,
				ignore_conflict: 0,
				ful_fill: 1,
			});
			// this.initSignature();
			if (this.uuid) {
				this.getCheckedOut();
			}
			if (this.cartUuid) {
				this.getCartCheckedOut();
			}
		},
		initSignature() {
			const _this = this;
			_this.$nextTick(() => {
				let ccanvas = _this.$refs["cc_signature"];
				let ccanvasDiv = _this.$refs["signature_div"];
				ccanvas.setAttribute("width", 500);
				ccanvasDiv.setAttribute("style", "width:" + "500px");
				_this.signature = new SignaturePad(ccanvas);
			});
		},
		getRelatedDetails(type, typeUuid) {
			let url = "";
			if (type == "Asset") {
				url = `asset/${typeUuid}`;
			} else if (type == "Asset Stock") {
				url = `asset-stock/${typeUuid}`;
			} else {
				url = `inventory/${typeUuid}`;
			}

			this.pageLoading = true;
			ApiService.get(url)
				.then(({ data }) => {
					this.relatedDetails = data;
					if (type != "Asset") {
						this.relatedDetails.cost_price_formatted = data.price_add_stock_formatted;
					}
				})
				.catch((error) => {
					//this.$emit("error", error);
					this.logError(error);
				})
				.finally(() => {
					this.pageLoading = false;
				});
		},
		getCheckedOut() {
			this.pageLoading = true;
			ApiService.get(`checkout/${this.uuid}`)
				.then(({ data }) => {
					this.checkout = new Object({
						duration: 1,
						duration_type: "week",
						return_on: this.formatDateTimeRaw(data.return_on),
						indefinitely: data.indefinitely,
						member: data.member,
						location: data.location,
						comment: data.description,
						ignore_conflict: data.ignore_conflict,
						ful_fill: data.ful_fill,
					});
					this.endpoint = data.asset && data.asset.id > 0 ? "asset" : "asset-stock";
					this.typeUuid = data.asset && data.asset.id > 0 ? data.asset.uuid : data.asset_stock.uuid;
					this.type = data.asset && data.asset.id > 0 ? "Asset" : "asset-stock";
					this.checkoutUuid = data.uuid;

					this.getRelatedDetails(this.type, this.typeUuid);
				})
				.catch((error) => {
					this.$emit("error", error);
					this.logError(error);
				})
				.finally(() => {
					this.pageLoading = false;
				});
		},
		getCartCheckedOut() {
			this.pageLoading = true;
			ApiService.get(`cart/${this.cartUuid}`)
				.then(({ data }) => {
					this.checkout = new Object({
						duration: 1,
						duration_type: "week",
						return_on: this.formatDateTimeRaw(data.reserve_to),
						indefinitely: data.indefinitely,
						member: data.member,
						location: data.location,
						comment: data.description,
						ignore_conflict: data.ignore_conflict,
						ful_fill: data.ful_fill,
					});
					this.endpoint = data.asset && data.asset.id > 0 ? "asset" : "asset-stock";
					this.typeUuid = data.asset && data.asset.id > 0 ? data.asset.uuid : data.asset_stock.uuid;
					this.type = data.asset && data.asset.id > 0 ? "Asset" : "asset-stock";
					this.checkoutUuid = data.uuid;

					this.getRelatedDetails(this.type, this.typeUuid);
				})
				.catch((error) => {
					this.$emit("error", error);
					this.logError(error);
				})
				.finally(() => {
					this.pageLoading = false;
				});
		},
		updateDuration() {
			let days = 1;

			let { duration_type, duration } = this.checkout;

			switch (duration_type) {
				case "day":
					days = toSafeInteger(duration);
					break;
				case "week":
					days = 7 * toSafeInteger(duration);
					break;
				case "month":
					days = 30 * toSafeInteger(duration);
					break;
			}

			this.$nextTick(() => {
				const date = MomentJS().add(days, "days");
				this.checkout.return_on = this.formatDateTimeRaw(date);
			});
		},
		manageLocation() {
			console.log("manageLocation");
		},
		getReservations() {
			this.reservationList = [];

			if (!this.showReservation) {
				return false;
			}

			this.reservationLoading = true;

			ApiService.get(`${this.endpoint}/${this.typeUuid}/reservation`)
				.then(({ data }) => {
					this.reservationList = data;
				})
				.catch((error) => {
					this.$emit("error", error);
					this.logError(error);
				})
				.finally(() => {
					this.reservationLoading = false;
				});
		},
		onSubmit() {
			const _this = this;

			const formErrors = _this.validateForm(_this.$refs.checkoutForm);
			if (formErrors.length) {
				_this.$store.commit(SET_ERROR, _this.errors.concat(formErrors));
				return false;
			}

			if (!_this.$refs.checkoutForm.validate()) {
				return false;
			}

			this.pageLoading = true;

			let requestUrl = `${this.endpoint}/${this.typeUuid}/checkout`;
			if (this.reservationUuid && this.reservationUuid != "") {
				requestUrl = `${this.endpoint}/${this.typeUuid}/reservation/${this.reservationUuid}/approve`;
			}
			this.checkout.reservation_uuid = this.reservationUuid;
			this.checkout.signature = null; //this.signature.toDataURL();
			ApiService.patch(requestUrl, this.checkout)
				.then(() => {
					this.$emit("success");
					EventBus.$emit("reload:asset-reservation", true);
				})
				.catch((error) => {
					this.$emit("error", error);
					this.logError(error);
				})
				.finally(() => {
					this.pageLoading = false;
				});
		},
		addToCart(replace = false) {
			const _this = this;

			if (_this.$refs["checkoutForm"]) {
				const formErrors = _this.validateForm(_this.$refs.checkoutForm);

				if (formErrors.length) {
					_this.$store.commit(SET_ERROR, _this.errors.concat(formErrors));
					return false;
				}

				if (!_this.$refs.checkoutForm.validate()) {
					return false;
				}
			}

			if (_this.type == "Asset Stock" && _this.relatedDetails.stock_level < _this.checkout.quantity) {
				_this.$store.commit(SET_ERROR, [
					{ model: true, message: `Max ${_this.relatedDetails.stock_level} Quantity Available.` },
				]);
				return false;
			}

			if (_this.type == "Perishable" && _this.relatedDetails.initial_stock < _this.checkout.quantity) {
				_this.$store.commit(SET_ERROR, [
					{ model: true, message: `Max ${_this.relatedDetails.initial_stock} Quantity Available.` },
				]);
				return false;
			}

			_this.pageLoading = true;

			let requestUrl = `cart/${_this.typeUuid}/checkout`;
			const cartData = {
				reserve_to: _this.checkout.return_on,
				indefinitely: _this.checkout.indefinitely,
				member: _this.checkout.member,
				attachment: _this.checkout.attachment,
				reason: _this.checkout.reason,
				duration: _this.checkout.duration,
				duration_type: _this.checkout.duration_type,
				comment: _this.checkout.comment,
				ignore_conflict: _this.checkout.ignore_conflict,
				ful_fill: _this.checkout.ful_fill,
				signature: null, //_this.signature.toDataURL(),
				related_type: _this.type == "Perishable" ? "inventory" : _this.type,
				quantity: _this.checkout.quantity,
				replace: replace,
			};

			ApiService.post(requestUrl, cartData)
				.then(({ data }) => {
					if (data && data.exists) {
						_this.$emit("close", true);
						_this
							.$swal({
								title: "Another incharge officer details exist in cart, Do you want to replace it?",
								icon: "question",
								showCancelButton: true,
								confirmButtonText: "Replace",
								confirmButtonClass: "bg-danger",
							})
							.then((result) => {
								if (result.isConfirmed) {
									_this.addToCart(true);
								}
							});
					} else {
						_this.$store.commit(SET_MESSAGE, [
							{ model: true, message: `Success ! ${_this.type} has been successfully added to cart.` },
						]);
						_this.$emit("success", true);
						EventBus.$emit("reload:cart", true);
						EventBus.$emit("reload:asset-reservation", true);
						store.dispatch(CART_COUNT);
					}
				})
				.catch((error) => {
					_this.$emit("error", error);
					_this.logError(error);
				})
				.finally(() => {
					_this.pageLoading = false;
				});
		},
		onUpdateSubmit() {
			const _this = this;

			const formErrors = _this.validateForm(_this.$refs.checkoutForm);
			if (formErrors.length) {
				_this.$store.commit(SET_ERROR, _this.errors.concat(formErrors));
				return false;
			}

			if (!_this.$refs.checkoutForm.validate()) {
				return false;
			}

			this.pageLoading = true;

			let requestUrl = `${this.endpoint}/${this.typeUuid}/checkout/${this.checkoutUuid}`;
			this.checkout.reservation_uuid = this.reservationUuid;
			ApiService.put(requestUrl, this.checkout)
				.then(() => {
					this.$emit("success");
					EventBus.$emit("reload:asset-reservation", true);
					_this.checkout = {
						duration: 1,
						duration_type: "week",
						return_on: null,
						indefinitely: 0,
						member: 0,
						location: 0,
						comment: null,
						ignore_conflict: 0,
						ful_fill: 1,
						reservation_uuid: null,
					};
				})
				.catch((error) => {
					this.$emit("error", error);
					this.logError(error);
				})
				.finally(() => {
					this.pageLoading = false;
				});
		},
		updateCart(replace = false) {
			const _this = this;

			const formErrors = _this.validateForm(_this.$refs.checkoutForm);
			if (formErrors.length) {
				_this.$store.commit(SET_ERROR, _this.errors.concat(formErrors));
				return false;
			}

			if (!_this.$refs.checkoutForm.validate()) {
				return false;
			}

			this.pageLoading = true;

			let requestUrl = `cart/${this.typeUuid}/checkout/${this.cartUuid}`;
			const cartData = {
				reserve_to: this.checkout.return_on,
				indefinitely: this.checkout.indefinitely,
				member: this.checkout.member,
				attachment: this.checkout.attachment,
				reason: this.checkout.reason,
				comment: this.checkout.comment,
				ignore_conflict: this.checkout.ignore_conflict,
				ful_fill: this.checkout.ful_fill,
				signature: null, // this.signature.toDataURL(),
				related_type: this.type,
				quantity: this.checkout.quantity,
				replace: replace,
			};

			ApiService.put(requestUrl, cartData)
				.then(({ data }) => {
					if (data && data.exists) {
						this.$emit("close", true);
						this.$swal({
							title: "Another member details exist in cart, Do you want to replace it?",
							icon: "question",
							showCancelButton: true,
							confirmButtonText: "Replace",
							confirmButtonClass: "bg-danger",
						}).then((result) => {
							if (result.isConfirmed) {
								this.updateCart(true);
							}
						});
					} else {
						this.$store.commit(SET_MESSAGE, [
							{ model: true, message: `Success ! ${this.type} has been successfully added to cart.` },
						]);
						this.$emit("success");
						EventBus.$emit("reload:asset-reservation", true);
						store.dispatch(CART_COUNT);
					}
				})
				.catch((error) => {
					this.$emit("error", error);
					this.logError(error);
				})
				.finally(() => {
					this.pageLoading = false;
				});
		},
	},
	mounted() {
		this.locationList = this.localDB("locations", []);
		this.memberList = this.localDB("members", []);
	},
	computed: {
		...mapGetters(["errors", "localDB"]),
		dialogWidth() {
			return toSafeInteger((document.body.clientWidth / 100) * 50);
		},
	},
};
</script>
